import React, { useState, useEffect } from 'react';
import { fetchOrganizationTickets, checkInTicket } from '../../apiClient';
import { FaSearch, FaFilter, FaEye, FaCheck, FaTimes, FaCalendarAlt, FaMapMarkerAlt, FaUser, FaTicketAlt, FaCheckCircle, FaClock } from 'react-icons/fa';

const OrganizationTicketsTab = ({ organizationId }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  
  // Pagination state (now handled by server)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    searchTerm: '',
    vendorId: '',
    eventId: '',
    dateFrom: '',
    dateTo: '',
    checkedInStatus: 'all' // 'all', 'checked-in', 'not-checked-in'
  });

  useEffect(() => {
    fetchTicketsData();
  }, [organizationId]);

  // Fetch data when filters or pagination change (only auto-trigger for dropdowns)
  useEffect(() => {
    fetchTicketsData();
  }, [filters.vendorId, filters.eventId, filters.checkedInStatus, pagination.currentPage]);

  const fetchTicketsData = async () => {
    try {
      setLoading(true);
      
      // Prepare API options with current filters and pagination
      const options = {
        page: pagination.currentPage,
        limit: pagination.limit,
        search: filters.searchTerm,
        vendorId: filters.vendorId,
        eventId: filters.eventId,
        checkedInStatus: filters.checkedInStatus,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      };
      
      // Fetch tickets with server-side pagination and filtering
      const { 
        data: ticketsData, 
        pagination: paginationData,
        filters: filtersData,
        error: ticketsError 
      } = await fetchOrganizationTickets(organizationId, options);
      
      console.log('Raw API Response:', { ticketsData, paginationData, filtersData, ticketsError });

      if (ticketsError) throw new Error(ticketsError);

      // Set tickets directly (no client-side filtering needed)
      const validTickets = ticketsData || [];
      setTickets(validTickets);
      
      // Update pagination state
      if (paginationData) {
        setPagination(paginationData);
      }
      
      // Set vendors and events from server response (for filter dropdowns)
      if (filtersData) {
        setVendors(filtersData.vendors || []);
        setEvents(filtersData.events || []);
      }
      
      console.log('Tickets loaded:', validTickets.length);
      console.log('Pagination:', paginationData);

    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes - reset to page 1 when filters change
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Handle manual input changes (search term and dates) - don't auto-trigger
  const handleManualFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    // Don't trigger fetch automatically
  };

  // Manual search function for search term and date filters
  const handleManualSearch = () => {
    // Reset to first page when searching
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    // Trigger data fetch
    fetchTicketsData();
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleManualSearch();
    }
  };

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      vendorId: '',
      eventId: '',
      dateFrom: '',
      dateTo: '',
      checkedInStatus: 'all'
    });
    
    // Reset to first page and trigger search
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    // Trigger data fetch after clearing
    setTimeout(() => fetchTicketsData(), 0);
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCheckIn = async (ticketId) => {
    try {
      setIsCheckingIn(true);
      
      const { data: checkedInData, error } = await checkInTicket(ticketId);

      if (error) throw new Error(error);

      // Update local state with the returned data
      const now = checkedInData.checked_in_at;
      setTickets(prev => prev.map(ticket => 
        ticket.id === ticketId 
          ? { ...ticket, checked_in: true, checked_in_at: now }
          : ticket
      ));

      // Update selected ticket if it's the one being checked in
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, checked_in: true, checked_in_at: now }));
      }

    } catch (error) {
      console.error('Error checking in ticket:', error);
      alert('Failed to check in ticket: ' + error.message);
    } finally {
      setIsCheckingIn(false);
    }
  };

  // Server-side pagination - no calculations needed, use tickets directly
  const currentTickets = tickets;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Ticket Management</h2>
        <div className="text-sm text-gray-600">
          {pagination.totalRecords} total tickets
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
          {/* Search */}
          <div className="xl:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Name, email, ticket ID..."
                  value={filters.searchTerm}
                  onChange={(e) => handleManualFilterChange('searchTerm', e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <FaSearch className="absolute right-3 top-3 text-gray-400" />
              </div>
              <button
                onClick={handleManualSearch}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2"
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>
            </div>
          </div>

          {/* Vendor Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <select
              value={filters.vendorId}
              onChange={(e) => handleFilterChange('vendorId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Vendors</option>
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.company_name}
                </option>
              ))}
            </select>
          </div>

          {/* Event Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
            <select
              value={filters.eventId}
              onChange={(e) => handleFilterChange('eventId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Events</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </div>

          {/* Check-in Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.checkedInStatus}
              onChange={(e) => handleFilterChange('checkedInStatus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Tickets</option>
              <option value="checked-in">Checked In</option>
              <option value="not-checked-in">Not Checked In</option>
            </select>
          </div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleManualFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleManualFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Clear Filters */}
        <button
          onClick={clearFilters}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
        >
          Clear All Filters
        </button>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attendee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchase Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentTickets.map((ticket) => (
                <tr 
                  key={ticket.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleTicketClick(ticket)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FaTicketAlt className="text-blue-500 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          #{ticket.id.slice(-8)}
                        </div>
                        <div className="text-sm text-gray-500">
                          ${ticket.price_paid}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {ticket.first_name} {ticket.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {ticket.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {ticket.events?.title || 'Unknown Event'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {ticket.events?.start_date ? formatDate(ticket.events.start_date) : 'No date'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {ticket.vendors?.logoURL ? (
                        <img 
                          src={ticket.vendors.logoURL} 
                          alt={ticket.vendors.company_name}
                          className="w-8 h-8 rounded-full object-cover mr-2"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                          <span className="text-xs font-bold text-gray-400">
                            {ticket.vendors?.company_name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      <span className="text-sm text-gray-900">
                        {ticket.vendors?.company_name || 'Unknown Vendor'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(ticket.purchase_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {ticket.checked_in ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FaCheckCircle className="mr-1" />
                        Checked In
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <FaClock className="mr-1" />
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTicketClick(ticket);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <FaEye />
                    </button>
                    {!ticket.checked_in && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCheckIn(ticket.id);
                        }}
                        disabled={isCheckingIn}
                        className="text-green-600 hover:text-green-900 disabled:text-gray-400"
                      >
                        <FaCheck />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(Math.max(pagination.currentPage - 1, 1))}
                  disabled={!pagination.hasPreviousPage}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(pagination.currentPage + 1, pagination.totalPages))}
                  disabled={!pagination.hasNextPage}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.limit) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)}</span> of{' '}
                    <span className="font-medium">{pagination.totalRecords}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(Math.max(pagination.currentPage - 1, 1))}
                      disabled={!pagination.hasPreviousPage}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Previous
                    </button>
                    {[...Array(pagination.totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.currentPage === index + 1
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.currentPage + 1, pagination.totalPages))}
                      disabled={!pagination.hasNextPage}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {tickets.length === 0 && !loading && (
        <div className="text-center py-12">
          <FaTicketAlt className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {pagination.totalRecords === 0 
              ? "No tickets have been sold yet." 
              : "Try adjusting your filters to see more results."
            }
          </p>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {isModalOpen && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Ticket Details</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Ticket Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Ticket Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Ticket ID:</span>
                        <span className="text-sm font-medium">#{selectedTicket.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Price Paid:</span>
                        <span className="text-sm font-medium">${selectedTicket.price_paid}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Ticket Type:</span>
                        <span className="text-sm font-medium">{selectedTicket.ticket_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Purchase Date:</span>
                        <span className="text-sm font-medium">{formatDate(selectedTicket.purchase_date)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Attendee Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Name:</span>
                        <span className="text-sm font-medium">{selectedTicket.first_name} {selectedTicket.last_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Email:</span>
                        <span className="text-sm font-medium">{selectedTicket.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="text-sm font-medium">{selectedTicket.phone || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Event Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Event Information</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-3">
                      <FaCalendarAlt className="text-blue-500 mr-2" />
                      <span className="font-medium">{selectedTicket.events?.title || 'Unknown Event'}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="text-gray-400 mr-2" />
                        <span>{selectedTicket.events?.location || 'No location'}</span>
                      </div>
                      <div className="flex items-center">
                        <FaClock className="text-gray-400 mr-2" />
                        <span>{selectedTicket.events?.start_date ? formatDate(selectedTicket.events.start_date) : 'No date'}</span>
                      </div>
                      <div className="flex items-center">
                        <FaTicketAlt className="text-gray-400 mr-2" />
                        <span>{selectedTicket.events?.event_type || 'Unknown type'}</span>
                      </div>
                      <div className="flex items-center">
                        <FaUser className="text-gray-400 mr-2" />
                        <span>{selectedTicket.vendors?.company_name || 'Unknown Vendor'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Check-in Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Check-in Status</h4>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {selectedTicket.checked_in ? (
                        <>
                          <FaCheckCircle className="text-green-500 mr-2" />
                          <div>
                            <span className="font-medium text-green-700">Checked In</span>
                            <div className="text-sm text-gray-600">
                              {formatDate(selectedTicket.checked_in_at)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <FaClock className="text-yellow-500 mr-2" />
                          <span className="font-medium text-yellow-700">Not Checked In</span>
                        </>
                      )}
                    </div>
                    {!selectedTicket.checked_in && (
                      <button
                        onClick={() => handleCheckIn(selectedTicket.id)}
                        disabled={isCheckingIn}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                      >
                        <FaCheck className="mr-2" />
                        {isCheckingIn ? 'Checking In...' : 'Check In'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationTicketsTab; 